import useFormatter from "@/hooks/useFormatter";
import useConfirm from "@/hooks/useConfirm";
import { clientsKeys } from "@/query-keys";
import { services } from "@/services";
import { createFileRoute, Link, Outlet, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, PhoneIcon, Mail, Scale, User, Calendar, Flag, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import useClientQuery from "../_hooks/useClientQuery";
import useClientMutation from "../_hooks/useClientMutation";
import FlagClientForm from "../_components/FlagClientForm";

export const Route = createFileRoute("/_private/clients/$clientId")({
  loader: async ({ params, context: { queryClient } }) => {
    const client = await queryClient.ensureQueryData({
      queryKey: clientsKeys.detail(params.clientId),
      queryFn: () => services.getClient(params.clientId),
    });
    return { client };
  },
  component: ClientDetailLayout,
});

function ClientDetailLayout() {
  const { t } = useTranslation();
  const formatter = useFormatter();
  const navigate = useNavigate();
  const { clientId } = Route.useParams();
  const { client: initialClient } = Route.useLoaderData();
  const { data: client } = useClientQuery(clientId, initialClient);
  const { deleteClient, isDeleting } = useClientMutation();

  const deleteConfirm = useConfirm({
    title: t("clients.confirm.delete.title"),
    description: t("clients.confirm.delete.description"),
    isPending: isDeleting,
  });

  const handleDelete = () =>
    deleteConfirm.confirm(async () => {
      await deleteClient(clientId);
      navigate({ to: "/clients" });
    });

  if (!client) return null;

  return (
    <div className="flex-1 flex flex-col gap-8 pb-8">
      <div className="flex flex-col gap-4">
        <Link
          to="/clients"
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors w-fit"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("transactions.detail.back")}
        </Link>
        <div className="space-y-4 sm:space-y-2">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                <Scale className="h-5 w-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold tracking-tight capitalize">
                    {client.name ?? t("common.notAvailable")}
                  </h1>
                  {client.isFlagged && (
                    <Badge variant="destructive">
                      <Flag className="h-3 w-3 fill-current" />
                      {client.flagReason || t("clients.flag.flagged")}
                    </Badge>
                  )}
                  {client.deletedAt && (
                    <Badge variant="destructive">
                      <Trash2 className="h-3 w-3" />
                      {t("common.deleted")}
                    </Badge>
                  )}
                </div>
                {(client.phone || client.email) && (
                  <div className="flex items-center gap-3 mt-0.5 text-sm text-muted-foreground">
                    {client.phone && (
                      <span className="flex items-center gap-1.5" dir="ltr">
                        <PhoneIcon className="h-3.5 w-3.5" />
                        {formatter.phone(client.phone)}
                      </span>
                    )}
                    {client.email && (
                      <span className="flex items-center gap-1.5">
                        <Mail className="h-3.5 w-3.5" />
                        {client.email}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <FlagClientForm />
              <Button variant="destructive" size="sm" onClick={handleDelete} disabled={!!client.deletedAt}>
                <Trash2 className="h-3.5 w-3.5" />
                {t("common.delete")}
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            {client.createdByName && (
              <span className="flex items-center gap-1.5">
                <User className="h-3.5 w-3.5" />
                {client.createdByName}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              {formatter.dateTime(client.createdAt)}
            </span>
          </div>
        </div>
      </div>

      <Outlet />

      {deleteConfirm.confirmDialog}
    </div>
  );
}
